import type { SpringOptions } from "framer-motion";
import { useRef } from "react";
import { motion, useMotionValue, useSpring } from "framer-motion";

interface TiltedCapabilityCardProps {
  title: string;
  description: string;
  titleColor?: string;
  containerHeight?: React.CSSProperties["height"];
  scaleOnHover?: number;
  rotateAmplitude?: number;
  showMobileWarning?: boolean;
}

const springValues: SpringOptions = {
  damping: 30,
  stiffness: 150,
  mass: 1.5,
};

export default function TiltedCapabilityCard({
  title,
  description,
  titleColor = "text-primary-700",
  containerHeight = "280px",
  scaleOnHover = 1.02,
  rotateAmplitude = 8,
  showMobileWarning = false,
}: TiltedCapabilityCardProps) {
  const ref = useRef<HTMLDivElement>(null);
  const rotateX = useSpring(useMotionValue(0), springValues);
  const rotateY = useSpring(useMotionValue(0), springValues);
  const scale = useSpring(1, springValues);

  function handleMouse(e: React.MouseEvent<HTMLDivElement>) {
    if (!ref.current) return;

    const rect = ref.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left - rect.width / 2;
    const offsetY = e.clientY - rect.top - rect.height / 2;

    const rotationX = (offsetY / (rect.height / 2)) * -rotateAmplitude;
    const rotationY = (offsetX / (rect.width / 2)) * rotateAmplitude;

    rotateX.set(rotationX);
    rotateY.set(rotationY);
  }

  function handleMouseEnter() {
    scale.set(scaleOnHover);
  }

  function handleMouseLeave() {
    scale.set(1);
    rotateX.set(0);
    rotateY.set(0);
  }

  return (
    <div
      ref={ref}
      className="relative w-full [perspective:1000px] flex items-center justify-center"
      style={{ height: containerHeight }}
      onMouseMove={handleMouse}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
    >
      {showMobileWarning && (
        <div className="absolute top-4 text-center text-xs block sm:hidden text-gray-500">
          Interactive on desktop
        </div>
      )}

      <motion.div
        className="w-full h-full bg-white p-6 rounded-lg shadow-md hover:shadow-xl transition-shadow duration-300 [transform-style:preserve-3d] border border-gray-100"
        style={{
          rotateX,
          rotateY,
          scale,
        }}
      >
        <div className="h-full flex flex-col">
          <h3 className={`text-xl font-semibold mb-4 ${titleColor}`}>
            {title}
          </h3>
          <p className="text-gray-600 leading-relaxed flex-1">
            {description}
          </p>
          
          {/* Subtle accent element */}
          <div className="mt-4 w-12 h-1 bg-gradient-to-r from-accent-400 to-secondary-400 rounded-full opacity-60"></div>
        </div>
      </motion.div>
    </div>
  );
}